// ADM-06 (live) — Application detail for backend-submitted applications.
//
// Differs from the seeded ApplicationDetailPage in that approve/reject
// calls the FastAPI backend, which persists status changes AND triggers
// real Resend emails (KYC invite on approve).

import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, CheckCircle2, XCircle, Mail, MapPin, Phone, Calendar, Lock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { StatusBadge } from "@/components/StatusBadge";
import { format } from "@/lib/brand-tokens";
import { getLiveApplication, approveLiveApplication, rejectLiveApplication } from "@/lib/onboarding-api";
import { Gated } from "@/components/Gated";
import { toast } from "sonner";

const REJECT_TEMPLATES = [
  { id: "source", label: "Source-of-cards unclear",            text: "We weren't able to verify the source of your cards. Please apply again with more detail on where the cards originate." },
  { id: "kyc",    label: "ID verification likely to fail",     text: "Our preliminary identity check raised concerns. We're unable to approve at this time." },
  { id: "risk",   label: "High-risk pattern",                   text: "Based on the information provided, this application doesn't fit our current member profile." },
  { id: "tos",    label: "Terms-of-service violation history", text: "We've identified a prior violation of our Terms of Service associated with this profile." },
];

export default function LiveApplicationDetailPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen,  setRejectOpen]  = useState(false);
  const [rejectTpl,   setRejectTpl]   = useState("source");
  const [rejectText,  setRejectText]  = useState(REJECT_TEMPLATES[0].text);

  const reload = async () => {
    try {
      setApp(await getLiveApplication(appId));
    } catch (e) {
      setError(e.message);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [appId]);

  const onTpl = (id) => {
    setRejectTpl(id);
    setRejectText(REJECT_TEMPLATES.find((t) => t.id === id)?.text || "");
  };

  const approve = async () => {
    setBusy(true);
    setApproveOpen(false);
    try {
      const updated = await approveLiveApplication(appId);
      setApp(updated);
      toast.success("Approved — KYC invite emailed", {
        description: `Sent to ${updated.email}${updated.lastEmail?.bcc ? ` (BCC: ${updated.lastEmail.bcc.join(", ")})` : ""}`,
        duration: 6000,
      });
    } catch (e) {
      toast.error("Approval failed", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    setRejectOpen(false);
    try {
      const updated = await rejectLiveApplication(appId, rejectText);
      setApp(updated);
      toast.error(`Rejected ${updated.firstName} ${updated.lastName}`, { description: rejectText.slice(0, 80) });
      setTimeout(() => navigate("/admin/applications"), 800);
    } catch (e) {
      toast.error("Rejection failed", { description: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <AlertCircle className="size-10 text-red-600 mx-auto mb-3" />
        <h1 className="text-h2 font-brand text-ink">Couldn't load application</h1>
        <p className="mt-2 text-body-sm text-ink-muted">{error}</p>
        <Link to="/admin/applications" className="inline-flex items-center gap-1 text-brand-700 hover:text-brand-800 mt-5">
          <ChevronLeft className="size-4" /> Back to queue
        </Link>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="flex items-center justify-center py-24 text-ink-muted">
        <Loader2 className="size-5 animate-spin mr-2" /> Loading application…
      </div>
    );
  }

  const lastEmail = app.lastEmail;
  const isTerminal = app.status === "Rejected" || app.status === "Active";

  return (
    <div className="space-y-5 pb-12">
      <Breadcrumbs items={[
        { label: "Applications", to: "/admin/applications" },
        { label: `${app.firstName} ${app.lastName}` },
      ]} />

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="size-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-bold text-h3">
            {app.firstName[0]}{app.lastName[0]}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-brand text-h1 text-ink font-semibold">{app.firstName} {app.lastName}</h1>
              <StatusBadge entity="application" status={app.status} dot />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">Live application</span>
            </div>
            <div className="mt-1 text-caption text-ink-subtle">Application <span className="font-mono">{app.id}</span> · Submitted {format.relative(app.submittedAt)}</div>
          </div>
        </div>
        {!isTerminal && (
          <div className="flex items-center gap-2">
            <Gated action="applications.reject">
              <Button variant="outline" onClick={() => setRejectOpen(true)} disabled={busy}
                      data-testid="reject-app-btn"
                      className="border-red-200 text-red-700 hover:bg-red-50">
                <XCircle className="size-4 mr-1.5" /> Reject
              </Button>
            </Gated>
            <Gated action="applications.approve">
              <Button onClick={() => setApproveOpen(true)} disabled={busy || app.status !== "PendingReview"}
                      data-testid="approve-app-btn"
                      className="bg-brand-700 hover:bg-brand-800 text-white">
                <CheckCircle2 className="size-4 mr-1.5" /> Approve & send KYC
              </Button>
            </Gated>
          </div>
        )}
      </div>

      {/* Email transcript card — shows what was sent + when */}
      {lastEmail && (
        <div data-testid="email-trail" className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-start gap-3">
          <Mail className="size-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-body-sm font-semibold text-ink">
              Email sent — {lastEmail.kind === "kyc_invite" ? "KYC invite" : "Login credentials"}
            </div>
            <div className="text-caption text-ink-muted mt-0.5">
              To <span className="font-mono">{lastEmail.to}</span>
              {lastEmail.bcc?.length > 0 && <> · BCC <span className="font-mono">{lastEmail.bcc.join(", ")}</span></>}
              {lastEmail.subject && <> · <em className="text-ink-subtle">"{lastEmail.subject}"</em></>}
            </div>
            <div className="text-caption text-ink-hint mt-0.5">{format.date(lastEmail.at)} · Resend ID <span className="font-mono">{lastEmail.id}</span></div>
          </div>
        </div>
      )}

      {app.status === "ApprovedAwaitingKYC" && app.kycToken && (
        <div className="bg-sky-50 border border-sky-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <Lock className="size-5 text-sky-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-body-sm font-semibold text-ink">Awaiting KYC completion</div>
              <p className="mt-1 text-caption text-ink-muted">The applicant must click the KYC link in their email to verify their identity. KYC link (for testing):</p>
              <div className="mt-2 font-mono text-code break-all">
                <Link to={`/onboarding/kyc/${app.kycToken}`} target="_blank" className="text-sky-700 hover:underline">
                  /onboarding/kyc/{app.kycToken}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-surface border border-line rounded-xl p-6">
            <h2 className="text-h3 text-ink font-semibold">Identity</h2>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 text-body-sm">
              <Row icon={Mail} label="Email" value={app.email} mono />
              <Row icon={Phone} label="Phone" value={app.phone} mono />
              <Row icon={Calendar} label="Date of birth" value={app.dateOfBirth} />
              <Row icon={Lock} label="SSN last 4" value={`•••−••−${app.ssnLast4}`} mono />
              <Row icon={MapPin} label="Address" value={`${app.address.street}, ${app.address.city}, ${app.address.state} ${app.address.zip}`} className="col-span-2" />
            </dl>
          </div>

          <div className="bg-surface border border-line rounded-xl p-6">
            <h2 className="text-h3 text-ink font-semibold">Risk profile</h2>
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 text-body-sm">
              <Row label="Monthly volume (est.)" value={app.monthlyVolumeUSD ? format.currency(app.monthlyVolumeUSD) : "—"} />
              <Row label="Referral source"      value={app.referralSource || "—"} />
              <Row label="Source of cards"      value={app.sourceOfCards || "—"} className="col-span-2" />
              <Row label="Terms accepted"       value={app.termsAccepted ? "Yes" : "No"} />
            </dl>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-surface border border-line rounded-xl p-5">
            <div className="eyebrow">Review</div>
            <div className="mt-2 text-body-sm text-ink-muted">
              {app.reviewedAt
                ? <>Reviewed {format.relative(app.reviewedAt)} by <span className="font-mono">{app.reviewerId}</span></>
                : <>Awaiting review</>}
            </div>
            {app.rejectReason && (
              <div className="mt-3 text-caption text-red-700 bg-red-50 border border-red-200 rounded-md p-2.5 leading-relaxed">{app.rejectReason}</div>
            )}
          </div>
        </div>
      </div>

      {/* Approve confirmation modal */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Approve {app.firstName} {app.lastName}?</DialogTitle>
            <DialogDescription className="pt-1 leading-relaxed">
              We'll email a KYC link to <strong className="text-ink">{app.email}</strong> (and BCC the demo address). The applicant has 7 days to complete KYC.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={approve} disabled={busy} data-testid="confirm-approve-btn"
                    className="bg-brand-700 hover:bg-brand-800 text-white">
              {busy ? <><Loader2 className="size-4 mr-1.5 animate-spin" /> Sending email…</> : "Approve & send KYC"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-confirm-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Reject application</DialogTitle>
            <DialogDescription className="pt-1">Pick a template or write your own reason.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-2">
            <div className="flex flex-wrap gap-1.5">
              {REJECT_TEMPLATES.map((t) => (
                <button key={t.id} onClick={() => onTpl(t.id)}
                        className={`px-2.5 py-1 text-caption rounded-full border ${
                          rejectTpl === t.id ? "bg-ink border-ink text-white" : "bg-surface border-line text-ink-muted hover:border-ink hover:text-ink"
                        }`}>
                  {t.label}
                </button>
              ))}
            </div>
            <Textarea value={rejectText} onChange={(e) => setRejectText(e.target.value)} rows={4} data-testid="reject-reason-textarea" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={reject} disabled={busy || !rejectText.trim()} data-testid="confirm-reject-btn"
                    className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? <><Loader2 className="size-4 mr-1.5 animate-spin" /> Rejecting…</> : "Reject application"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ icon: Icon, label, value, mono, className = "" }) {
  return (
    <div className={className}>
      <dt className="flex items-center gap-1.5 text-caption text-ink-hint">{Icon && <Icon className="size-3" />}{label}</dt>
      <dd className={`mt-0.5 text-ink ${mono ? "font-mono text-code" : ""}`}>{value}</dd>
    </div>
  );
}
